import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import { Models } from '../../../../General/Utils/ModelsGeneral';
import GestorDocumentalModal from './GestorDocumentalModal';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import './../../../../../style/ContainersStyles.css';
import '../../../Styles/index.css';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Gestión de Permisos por Empleado";

export default function GestorDocumentalCatalog() {
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]); // Ahora serán filas agrupadas por Empleado
    const [allGestores, setAllGestores] = useState([]); // Todos los registros crudos

    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: 'Información',
        message: '',
        variant: 'info'
    });

    // Filtros / Paginación
    const [query, setQuery] = useState('');
    const [page, setPage] = useState(1);

    // Modal / Edición
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('new');
    const [selectedIdEmpleado, setSelectedIdEmpleado] = useState(null);
    const [selectedEmpleadoName, setSelectedEmpleadoName] = useState('');

    // Eliminación
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });

    const editorRef = useRef(null);

    // Scroll al editor cuando se abre
    useEffect(() => {
        if (modalOpen && editorRef.current) {
            editorRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [modalOpen, selectedIdEmpleado]);

    // Carga de datos
    const loadData = async () => {
        setLoading(true);
        try {
            const [resGestores, resEmpleados, resAreas, resPersonas] = await Promise.all([
                axios.get(ENDPOINTS.gestorDocumental),
                axios.get(ENDPOINTS.empleados),
                axios.get(ENDPOINTS.areasNegocios),
                axios.get(ENDPOINTS.personas)
            ]);

            const listaPersonas = (resPersonas.data || []).map(p => Models.personas(p));
            const listaEmpleados = (resEmpleados.data || []).map(e => Models.empleado(e));
            const listaAreas = (resAreas.data || []).map(a => Models.areasNegocios(a));
            const listaGestoresRaw = resGestores.data || [];

            // Manual normalization for GestorDocumental
            const listaGestores = listaGestoresRaw.map(g => ({
                idGestorDocumental: g.idGestorDocumental ?? g.IdGestorDocumental ?? 0,
                idEmpleado: g.idEmpleado ?? g.IdEmpleado ?? 0,
                idAreaNegocio: g.idAreaNegocio ?? g.IdAreaNegocio ?? 0,
                activo: g.activo ?? g.Activo ?? false
            }));

            setAllGestores(listaGestores);

            // Agrupación por Empleado
            // Mapa: idEmpleado -> { idEmpleado, nombreCompleto, areasCount, areasNombres (array) }
            const grouped = {};

            listaGestores.forEach(g => {
                if (!g.activo) return; // Solo activos? Supondremos que sí

                if (!grouped[g.idEmpleado]) {
                    const empleado = listaEmpleados.find(e => e.idEmpleado === g.idEmpleado);
                    const persona = empleado ? listaPersonas.find(p => p.idPersona === empleado.idPersona) : null;
                    const nombreCompleto = persona
                        ? `${persona.nombre || ''} ${persona.apellidoPaterno || ''} ${persona.apellidoMaterno || ''}`.trim()
                        : `Empleado #${g.idEmpleado}`;

                    grouped[g.idEmpleado] = {
                        idEmpleado: g.idEmpleado,
                        nombreCompleto: nombreCompleto,
                        areas: []
                    };
                }

                const area = listaAreas.find(a => a.idAreaNegocio === g.idAreaNegocio);
                if (area) {
                    grouped[g.idEmpleado].areas.push(area.nombre);
                }
            });

            // Convertir a array
            const data = Object.values(grouped).map(item => ({
                ...item,
                areasDisplay: item.areas.slice(0, 3).join(", ") + (item.areas.length > 3 ? ` y ${item.areas.length - 3} más` : "")
            }));

            // Ordenar por nombre
            data.sort((a, b) => a.nombreCompleto.localeCompare(b.nombreCompleto));

            setRows(data);

        } catch (e) {
            console.error(e);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Error al cargar datos.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadData(); }, []);

    // Filtrado
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(r =>
            (r.nombreCompleto || '').toLowerCase().includes(q) ||
            r.areas.some(a => a.toLowerCase().includes(q))
        );
    }, [rows, query]);

    // Paginación
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

    const paged = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filtered.slice(start, start + PAGE_SIZE);
    }, [filtered, page]);

    // Acciones
    const handleNew = () => {
        setSelectedIdEmpleado(null);
        setSelectedEmpleadoName('');
        setModalMode('new');
        setModalOpen(true);
    };

    const handleEdit = (r) => {
        setSelectedIdEmpleado(r.idEmpleado);
        setSelectedEmpleadoName(r.nombreCompleto);
        setModalMode('edit');
        setModalOpen(true);
    };

    const askDelete = (r) => {
        setDeleteTarget({ id: r.idEmpleado, name: r.nombreCompleto });
        setDeleteOpen(true);
    };

    // Al eliminar un "Empleado" de la lista, en realidad eliminamos TODOS sus permisos de Gestor
    const doDelete = async () => {
        try {
            setLoading(true);
            // Buscar todos los registros de gestor para este empleado
            const registros = allGestores.filter(g => g.idEmpleado === deleteTarget.id);

            // Eliminar uno por uno (API limitation usually)
            const promises = registros.map(g => axios.delete(`${ENDPOINTS.gestorDocumental}/${g.idGestorDocumental}`));
            await Promise.all(promises);

            setDeleteOpen(false);
            await loadData();
        } catch (e) {
            console.error(e);
            setAlertDialog({
                open: true,
                title: 'Error',
                message: "Error al eliminar los permisos.",
                variant: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleModalClose = (shouldReload) => {
        setModalOpen(false);
        if (shouldReload) {
            loadData();
        }
    };

    // Componente Paginador simple
    const PageBtn = ({ label, onClick, active, disabled }) => (
        <button
            type="button"
            className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
            onClick={onClick}
            disabled={disabled}
        >
            {label}
        </button>
    );

    const pageNumbers = (() => {
        const maxToShow = 5;
        let start = Math.max(1, page - 2);
        let end = Math.min(totalPages, start + maxToShow - 1);
        start = Math.max(1, end - maxToShow + 1);
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    })();

    const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

    return (
        <div className="panel-sigma-main">
            <div className="roles-shell">
                <div className="roles-topbar">
                    <h2 className="roles-title">{nombreTabla}</h2>
                    <input
                        className="roles-search"
                        placeholder="Buscar por empleado o área..."
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                    />
                    <button className="btn-action" onClick={handleNew}>＋ Nuevo Asignación</button>
                </div>

                <div className="roles-table-wrap">
                    <div className="roles-table-scroller">
                        <table className="roles-table">
                            <thead>
                                <tr>
                                    <th>Empleado</th>
                                    <th>Áreas Asignadas</th>
                                    <th style={{ textAlign: 'center' }}>Editar</th>
                                    <th style={{ textAlign: 'center' }}>Borrar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.map(r => (
                                    <tr key={r.idEmpleado}>
                                        <td><strong>{normalizeValue(r.nombreCompleto)}</strong></td>
                                        <td>
                                            <span style={{ color: '#0f172a', fontSize: '0.95rem' }}>
                                                {r.areas.length} {r.areas.length === 1 ? 'Área asignada' : 'Áreas asignadas'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="rk-iconbtn rk-iconbtn--edit" onClick={() => handleEdit(r)} title="Gestionar Áreas">
                                                <img src={sigmaEdit} alt="Editar" className="rk-ico" />
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <button className="rk-iconbtn rk-iconbtn--del" onClick={() => askDelete(r)} title="Eliminar Todos los Permisos">
                                                <img src={sigmaDelete} alt="Borrar" className="rk-ico" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {!loading && paged.length === 0 && (
                                    <tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem' }}>Sin asignaciones.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="roles-pagination">
                    <div className="roles-pagination-left">
                        {filtered.length > 0
                            ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
                            : <span>0 resultados</span>}
                    </div>
                    <div className="roles-pagination-right">
                        <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
                        {pageNumbers.map(n => (
                            <PageBtn
                                key={n}
                                label={n}
                                onClick={() => setPage(n)}
                                active={n === page}
                            />
                        ))}
                        <PageBtn label="›" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                    </div>
                </div>

                {modalOpen && (
                    <div className="roles-editor-slot" ref={editorRef}>
                        <GestorDocumentalModal
                            mode={modalMode}
                            idEmpleado={selectedIdEmpleado}
                            nombreEmpleadoPre={selectedEmpleadoName}
                            onClose={handleModalClose}
                            inline
                        />
                    </div>
                )}
            </div>

            <ConfirmDeleteDialog
                open={deleteOpen}
                roleName={deleteTarget.name}
                onClose={() => setDeleteOpen(false)}
                onConfirm={doDelete}
            />

            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => {
                    setAlertDialog({ ...alertDialog, open: false });
                    if (alertDialog.onConfirm) alertDialog.onConfirm();
                }}
                buttonText="Aceptar"
            />
        </div>
    );
}
